import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Container,
    Typography,
    Paper,
    Alert,
    Grid,
    Link
} from '@mui/material';
import { FormTextField } from '../components/common/FormFields';
import { loginSchema } from '../services/utils/validation';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import type { LoginCredentials } from '../services/types/auth';
import PublicLayout from '../layouts/PublicLayout';

const Login: React.FC = () => {
    const { t } = useTranslation();
    const { login, error: authError } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [error, setError] = useState<string | null>(null);
    const message = location.state?.message;
    const returnTo = location.state?.returnTo;
    const quizId = location.state?.quizId;

    const onLoginSuccess = () => {
        if (returnTo && quizId) {
            handleStartQuiz(quizId);
        } else if (returnTo) {
            navigate(returnTo);
        } else {
            navigate('/dashboard');
        }
    };

    const handleStartQuiz = async (paperId: number) => {
        try {
            const response = await api.post('/quiz/attempts', { 
                paper_id: paperId
            });
            
            if (response.data && response.data.attempt_id) {
                navigate(`/quiz/${response.data.attempt_id}`);
            }
        } catch (error: any) {
            setError(error.response?.data?.error || t('quiz.startFailed'));
            navigate('/student');
        }
    };

    const handleSubmit = async (values: LoginCredentials) => {
        try {
            await login(values);
            onLoginSuccess();
        } catch (err) {
            console.error('Login failed:', err);
        }
    };

    const initialValues: LoginCredentials = {
        email: '',
        password: ''
    };

    return (
        <PublicLayout>
            <Container component="main" maxWidth="xs">
                <Box
                    sx={{
                        marginTop: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
                        <Typography component="h1" variant="h5" align="center" gutterBottom>
                            {t('auth.loginTitle')}
                        </Typography>

                        {message && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                {message}
                            </Alert>
                        )}

                        {(authError || error) && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {authError || error}
                            </Alert>
                        )}

                        <Formik
                            initialValues={initialValues}
                            validationSchema={loginSchema(t)}
                            onSubmit={handleSubmit}
                        >
                            {({ isSubmitting }) => (
                                <Form>
                                    <FormTextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="email"
                                        label={t('auth.email')}
                                        name="email"
                                        autoComplete="email"
                                        autoFocus
                                    />
                                    <FormTextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        name="password"
                                        label={t('auth.password')}
                                        type="password"
                                        id="password"
                                        autoComplete="current-password"
                                    />
                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        sx={{ mt: 3, mb: 2 }}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? t('auth.loggingIn') : t('common.login')}
                                    </Button>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Link
                                            component={RouterLink}
                                            to="/reset-password-request"
                                            variant="body2"
                                        >
                                            {t('auth.forgotPassword')}
                                        </Link>
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    </Paper>
                </Box>
            </Container>
        </PublicLayout>
    );
};

export default Login;
